import {
  GraphComponent,
  INode,
  LayoutDescriptor,
  ViewportDescriptor,
  ViewportLimitingMode,
} from '@ardoq/yfiles';
import { Rect, inside } from '@ardoq/graph';
import { dataModelId } from './graphComponentUtil';

const zoomViewport = (
  graphComponent: GraphComponent,
  zoomComponentId: string | null
) => {
  if (zoomComponentId) {
    const nodeToZoom = graphComponent.graph.nodes.find(
      node =>
        node.tag &&
        (node.tag.id === zoomComponentId ||
          dataModelId(node.tag) === zoomComponentId)
    );
    if (nodeToZoom) {
      zoomToNode(graphComponent, nodeToZoom);
    }
  }
};
const zoomToNode = (graphComponent: GraphComponent, node: INode) => {
  if (node) {
    const { zoom } = graphComponent;
    const {
      layout: { center },
    } = node;
    const { center: constrainedCenter } =
      graphComponent.viewportLimiter.limitViewport(
        graphComponent,
        new ViewportDescriptor(center, zoom),
        ViewportLimitingMode.PROGRAMMATIC_NAVIGATION
      );
    graphComponent.zoomTo(zoom, constrainedCenter);
  } else {
    graphComponent.fitContent();
  }
};
const getLayoutOrientation = (layout: LayoutDescriptor | null) =>
  layout?.name === 'HierarchicalLayout'
    ? layout.properties?.layoutOrientation
    : null;
const didLayoutOrientationChange = (
  layoutDescriptor: LayoutDescriptor,
  previousLayoutDescriptor: LayoutDescriptor | null
) =>
  getLayoutOrientation(layoutDescriptor) !==
  getLayoutOrientation(previousLayoutDescriptor);
export interface OnLayoutGraphArgs {
  graphComponent: GraphComponent;
  hasGraphUpdate: boolean;
  layoutDescriptor: LayoutDescriptor;
  previousLayoutDescriptor: LayoutDescriptor | null;
}

interface GetOnLayoutGraphArgs {
  /** ID of the node or group to zoom in on. */
  zoomComponentId: string | null;
}
/** Provides the common implementation of post-layout viewport adjustment. */
export const onLayoutGraphFactory =
  ({ zoomComponentId }: GetOnLayoutGraphArgs) =>
  ({
    graphComponent,
    layoutDescriptor,
    previousLayoutDescriptor,
    hasGraphUpdate,
  }: OnLayoutGraphArgs) => {
    const { contentBounds, viewport } = graphComponent;
    const isFullGraphDisplayed = inside(
      Rect.fromRectLike(contentBounds),
      Rect.fromRectLike(viewport)
    );
    const fitContent =
      didLayoutOrientationChange(layoutDescriptor, previousLayoutDescriptor) ||
      (!zoomComponentId && hasGraphUpdate) ||
      isFullGraphDisplayed;
    if (
      fitContent &&
      contentBounds.width > 0 &&
      contentBounds.height > 0 &&
      viewport.width > 0 &&
      viewport.height > 0 // fitContent will throw errors if you call it with a viewport or contentRect with no size.
    ) {
      graphComponent.fitContent();
      return;
    }

    if (zoomComponentId) {
      zoomViewport(graphComponent, zoomComponentId);
    }
  };
